import { motion } from "framer-motion";
import { MapPin, Building2, Globe2, Plane } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function Branches() {
  const { t } = useI18n();
  const { branches } = t;

  return (
    <section id="branches" className="bg-muted/40 border-y border-border" data-testid="branches-section">
      <div className="container-page section-y">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="overline">{branches.eyebrow}</span>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {branches.heading1} <br />
              <span className="text-primary">{branches.heading1Accent}</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 text-muted-foreground leading-relaxed"
          >
            {branches.description}
          </motion.p>
        </div>

        {/* KSA Network */}
        <div className="mb-14">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="text-2xl sm:text-3xl font-bold">{branches.ksaTitle}</h3>
            </div>
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {branches.ksaSubtitle}
            </span>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-border border border-border"
            data-testid="branches-ksa-grid"
          >
            {branches.ksa.map((b, i) => {
              const isHQ = b.role === "headquarters";
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className={`group relative p-6 transition-colors ${
                    isHQ
                      ? "bg-secondary text-secondary-foreground grain"
                      : "bg-card hover:bg-muted/60"
                  }`}
                  data-testid={`branch-ksa-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className={`h-10 w-10 flex items-center justify-center ${
                        isHQ ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                      }`}
                    >
                      <MapPin className="h-4 w-4" />
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                        isHQ
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted/60 text-muted-foreground border-border"
                      }`}
                    >
                      {isHQ ? branches.labelHQ : branches.labelBranch}
                    </span>
                  </div>
                  <div className="mt-6 text-2xl sm:text-3xl font-black leading-tight">
                    {b.city}
                  </div>
                  <div
                    className={`mt-1 text-xs uppercase tracking-[0.2em] ${
                      isHQ ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    {b.region}
                  </div>
                  <p
                    className={`mt-4 text-sm leading-relaxed ${
                      isHQ ? "text-secondary-foreground/70" : "text-foreground/75"
                    }`}
                  >
                    {b.note}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>

        {/* International Offices */}
        <div>
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-3">
              <Globe2 className="h-5 w-5 text-primary" />
              <h3 className="text-2xl sm:text-3xl font-bold">{branches.intlTitle}</h3>
            </div>
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {branches.intlSubtitle}
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="branches-intl-grid">
            {branches.intl.map((b, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative bg-card border border-border p-7 hover:border-primary transition-colors group"
                data-testid={`branch-intl-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 bg-primary/10 text-primary flex items-center justify-center">
                    <Plane className="h-4 w-4" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-muted/60 text-muted-foreground border border-border">
                    {branches.labelIntl}
                  </span>
                </div>
                <div className="mt-6 text-2xl sm:text-3xl font-black leading-tight">
                  {b.country}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {b.city}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground/75">{b.note}</p>
                <div className="mt-6 h-px w-full bg-border group-hover:bg-primary transition-colors" />
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
